import _extends from "@babel/runtime/helpers/esm/extends";
import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
import React, { useState, forwardRef, useRef } from 'react';
import { arrayOf, shape, oneOfType, string, number, func, bool, any } from 'prop-types';
import ProductCard from '../ProductCard';
import ProductListNotFound from './product-list-not-found';
import JsonLD, { productSchema } from '../../atoms/JsonLD';
import { ICON_SHOW_FULL_LIST, ICON_SHOW_MORE, LABEL_SHOW_FULL_LIST, LABEL_SHOW_MORE, MAGALU_DOMAIN } from '../../constants';
import { Container, List, ListItem, ListAction } from './product-list.styles';
const ProductList = /*#__PURE__*/forwardRef(function ProductList(_ref, ref) {
  var _ref2;

  let {
    hidePriceButtonAction,
    lazyLoadingOffset,
    onClickLoadMore,
    imgLazyLoading,
    showInCashInfo,
    showDiscount,
    showDiscountOnlyCashInfo,
    imageAltPrefix,
    imageWidth,
    imageHeight,
    onClickWishBtn,
    previewAction,
    showFullListBtn,
    enableShadow,
    displayMode,
    enableHover,
    showPreview,
    showWishBtn,
    showMoreBtn,
    hidePrice,
    notFound,
    onClick,
    onMouseDown,
    onProductView,
    partial,
    titleAs,
    domain,
    jsonLD,
    allowEmptyRating,
    data,
    gridGap,
    onThumbnailClick,
    colorMinLength,
    attributeStyle,
    offerTimerProps
  } = _ref,
      rest = _objectWithoutPropertiesLoose(_ref, ["hidePriceButtonAction", "lazyLoadingOffset", "onClickLoadMore", "imgLazyLoading", "showInCashInfo", "showDiscount", "showDiscountOnlyCashInfo", "imageAltPrefix", "imageWidth", "imageHeight", "onClickWishBtn", "previewAction", "showFullListBtn", "enableShadow", "displayMode", "enableHover", "showPreview", "showWishBtn", "showMoreBtn", "hidePrice", "notFound", "onClick", "onMouseDown", "onProductView", "partial", "titleAs", "domain", "jsonLD", "allowEmptyRating", "data", "gridGap", "onThumbnailClick", "colorMinLength", "attributeStyle", "offerTimerProps"]);

  (_ref2 = ref) !== null && _ref2 !== void 0 ? _ref2 : ref = useRef(null);
  const [containerWidth, setContainerWidth] = useState(0);
  const [collapsed, setCollapsed] = useState(Boolean(partial));
  const props = partial && !showFullListBtn && {
    collapse: true,
    trigger: collapsed,
    collapsedIn: partial
  };
  React.useEffect(() => {
    var _ref3;

    if ((_ref3 = ref) != null && _ref3.current) {
      setContainerWidth(ref.current.clientWidth);
    }

    setCollapsed(Boolean(partial));
    return () => setCollapsed(false);
  }, [partial]);
  if (notFound) return /*#__PURE__*/React.createElement(ProductListNotFound, notFound);
  const Products = data == null ? void 0 : data.map((item, index) => /*#__PURE__*/React.createElement(ListItem, {
    key: `product-item-${index}`
  }, /*#__PURE__*/React.createElement(ProductCard, _extends({
    key: `product-${item.variationId || item.id}-${index}`,
    hidePriceButtonAction: hidePriceButtonAction,
    lazyLoadingOffset: lazyLoadingOffset,
    imgLazyLoading: imgLazyLoading,
    showInCashInfo: showInCashInfo,
    imageAltPrefix: imageAltPrefix,
    imageWidth: imageWidth,
    imageHeight: imageHeight,
    enableShadow: enableShadow,
    displayMode: displayMode,
    domain: domain,
    enableHover: enableHover,
    showPreview: showPreview,
    showWishBtn: showWishBtn,
    hidePrice: hidePrice,
    onClick: onClick,
    onMouseDown: onMouseDown,
    onView: onProductView,
    titleAs: titleAs,
    index: index,
    showDiscount: showDiscount,
    showDiscountOnlyCashInfo: showDiscountOnlyCashInfo,
    onClickWishBtn: e => onClickWishBtn(e, item),
    previewAction: () => previewAction(item),
    allowEmptyRating: allowEmptyRating,
    containerWidth: containerWidth,
    onThumbnailClick: onThumbnailClick,
    colorMinLength: colorMinLength,
    attributeStyle: attributeStyle,
    offerTimerProps: offerTimerProps
  }, item))));
  const action = showFullListBtn ? {
    label: LABEL_SHOW_FULL_LIST,
    props: ICON_SHOW_FULL_LIST
  } : {
    label: LABEL_SHOW_MORE,
    props: ICON_SHOW_MORE
  };
  return /*#__PURE__*/React.createElement(Container, _extends({
    ref: ref,
    "data-testid": "product-list"
  }, rest), /*#__PURE__*/React.createElement(List, _extends({}, props, {
    variant: "light",
    displayMode: displayMode,
    gridGap: gridGap,
    renderAction: (collapsed || showFullListBtn || showMoreBtn) && partial < data.length && /*#__PURE__*/React.createElement(ListAction, _extends({
      onClick: () => {
        onClickLoadMore();
        setCollapsed(!collapsed);
      }
    }, action.props), action.label)
  }), Products), jsonLD && data && /*#__PURE__*/React.createElement(JsonLD, {
    schema: data.filter(item => {
      var _item$price;

      return (_item$price = item.price) == null ? void 0 : _item$price.price;
    }).map(item => productSchema({
      product: item,
      isList: true
    }, domain)),
    type: "ItemList"
  }));
});
process.env.NODE_ENV !== "production" ? ProductList.propTypes = {
  hidePriceButtonAction: func,
  lazyLoadingOffset: number,
  imageAltPrefix: string,
  imageWidth: number,
  imageHeight: number,
  onClickLoadMore: func,
  showFullListBtn: bool,
  imgLazyLoading: bool,
  showInCashInfo: bool,
  onClickWishBtn: func,
  displayMode: string,
  previewAction: func,
  enableShadow: bool,
  showPreview: bool,
  enableHover: bool,
  showWishBtn: bool,
  showMoreBtn: bool,
  hidePrice: bool,
  partial: number,
  titleAs: string,
  domain: string,
  onClick: func,
  onMouseDown: func,
  onProductView: func,
  jsonLD: bool,
  showDiscountOnlyCashInfo: bool,
  showDiscount: bool,
  gridGap: oneOfType([string, number]),
  data: arrayOf(shape({
    id: oneOfType([string, number]).isRequired
  })).isRequired,
  notFound: shape({
    notFound: any,
    notFoundSecondary: any
  }),
  allowEmptyRating: bool,
  onThumbnailClick: func,
  colorMinLength: number,
  attributeStyle: shape({}),
  offerTimerProps: shape({})
} : void 0;
ProductList.defaultProps = {
  hidePriceButtonAction: null,
  showFullListBtn: false,
  displayMode: 'gallery',
  domain: MAGALU_DOMAIN,
  lazyLoadingOffset: 50,
  showInCashInfo: true,
  imgLazyLoading: true,
  showWishBtn: false,
  showPreview: false,
  showMoreBtn: false,
  imageAltPrefix: '',
  hidePrice: false,
  showDiscountOnlyCashInfo: false,
  showDiscount: false,
  titleAs: 'h2',
  jsonLD: true,
  partial: 0,
  allowEmptyRating: false,
  onThumbnailClick: null,
  colorMinLength: 3,
  attributeStyle: {},
  onClickLoadMore: () => {},
  onClickWishBtn: () => {},
  previewAction: () => {},
  onClick: () => {},
  onMouseDown: () => {},
  onProductView: () => {}
};
export default ProductList;